// Rescue — "Prototype to Production" service: auditing and hardening AI prototypes for production.
import { ArrowRight, Check } from "lucide-react";
import Reveal from "../components/Reveal";

const PROBLEM_CHIPS = [
  "API keys stored in frontend code",
  "Admin endpoints without authentication",
  "Default or unencrypted database credentials",
  "No input validation or rate limiting",
  "Customer data not isolated between accounts",
  "No monitoring, alerting, or rollback plan",
];

interface Step { num: string; label: string; desc: string; }
const STEPS: Step[] = [
  {
    num: "01", label: "Audit",
    desc: "We read every file, every dependency, every data flow. You get a written report within five business days.",
  },
  {
    num: "02", label: "Scope",
    desc: "We tell you exactly what needs fixing, what it costs, and whether it's a clean-up or a partial rebuild.",
  },
  {
    num: "03", label: "Harden",
    desc: "Our senior engineers close the gaps: security, load, tenancy, data integrity, or direct your team to do it.",
  },
];

interface Metric { value: string; label: string; }
const METRICS: Metric[] = [
  { value: "5 days",      label: "Audit turnaround" },
  { value: "AU/NZ",       label: "Onshore engineers" },
  { value: "Fixed price", label: "Audit cost" },
];

export default function Rescue() {
  return (
    <section
      id="rescue"
      className="bg-black py-28 md:py-40 px-6 overflow-hidden relative"
      style={{ backgroundImage: "radial-gradient(ellipse at center, rgba(255,255,255,0.02) 0%, transparent 60%)" }}
    >
      <div className="max-w-6xl mx-auto">

        <Reveal>
          <div className="text-white/40 text-sm tracking-widest uppercase">
            Rescue · Hardening · Production
          </div>
        </Reveal>

        <Reveal delay={100}>
          <h2 className="font-instrument text-5xl md:text-7xl lg:text-8xl text-white tracking-tight mt-6 leading-[1.05]">
            Your prototype works.<br />
            Production is a <em className="italic-serif text-white/50">different</em> problem.
          </h2>
        </Reveal>

        <Reveal delay={220}>
          <p className="mt-8 text-white/60 text-base md:text-lg leading-relaxed max-w-2xl">
            Founders and teams ship fast with AI tools. That's the right call. But what comes out of Bolt,
            Lovable, Replit or Cursor is a working demo, not a production system. The gap between the two
            is wider than most teams expect, and the consequences of skipping it are real: data exposed,
            systems failing under load, security incidents that kill trust. We audit what you've built, tell
            you exactly what it costs to fix, and either fix it ourselves or direct your team through it.
          </p>
        </Reveal>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-12">
          {PROBLEM_CHIPS.map((chip, i) => (
            <Reveal key={chip} kind="reveal-soft" delay={i * 50}>
              <div className="liquid-glass rounded-2xl px-4 py-3 flex items-start gap-3 h-full">
                <div className="mt-0.5 w-5 h-5 rounded-full bg-white/10 flex items-center justify-center text-white/90 flex-none">
                  <Check size={12} />
                </div>
                <div className="text-white/80 text-sm leading-snug">{chip}</div>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 mt-12">

          <Reveal delay={150}>
            <div className="liquid-glass rounded-3xl p-6 md:p-8 h-full flex flex-col justify-center gap-7">
              {STEPS.map((step) => (
                <div key={step.num} className="flex items-start gap-4">
                  <div className="flex-none mt-0.5 bg-white/[0.06] rounded-full px-3 py-1 text-white/50 text-xs tracking-widest uppercase whitespace-nowrap">
                    {step.num} · {step.label}
                  </div>
                  <p className="text-white/60 text-sm leading-relaxed">{step.desc}</p>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal kind="reveal-x-right" delay={150}>
            <div className="grid grid-cols-3 lg:grid-cols-1 gap-3 lg:gap-4 h-full content-center">
              {METRICS.map((m) => (
                <div key={m.label} className="liquid-glass rounded-2xl p-4 text-center">
                  <div className="font-instrument text-white text-2xl md:text-3xl tracking-tight">{m.value}</div>
                  <div className="text-white/40 text-xs tracking-widest uppercase mt-1">{m.label}</div>
                </div>
              ))}
            </div>
          </Reveal>

        </div>

        <Reveal kind="reveal-soft" delay={400}>
          <div className="mt-10 flex flex-wrap gap-3">
            <a
              href="/#contact"
              className="rounded-full px-7 py-3 text-black text-sm font-medium bg-white hover:bg-white/90 transition-colors inline-flex items-center gap-2"
            >
              Book a prototype audit <ArrowRight size={16} />
            </a>
            <a
              href="#rescue"
              className="liquid-glass rounded-full px-8 py-3 text-white text-sm font-medium hover:bg-white/5 transition-colors"
            >
              See what we check
            </a>
          </div>
        </Reveal>

      </div>
    </section>
  );
}
